import { Dispatch, SetStateAction, useEffect } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import { Divider, Spin } from "antd";
import cn from "classnames";
import { ErrorField, FeatureToggle, RadioField, TitleField, TitlePage } from "~/components";
import {
  FormField,
  Wizard,
  UploadFile,
  AcceptPolitics
} from "~/pages/questionnaires/shared";
import { AttachmentsDetailsError, ClientId, NamesOfFieldEnum, Offer, OrgTypeEnum } from "~/typings/types";
import { configTitleFieldsOfCompany } from "../configTitleFields";
import { RulesFiles } from "../../../../shared/rules-files";

import "~/pages/questionnaires/shared/styles.css";
import styles from "~/pages/questionnaires/shared/Shared.module.scss";
import stylesStep from "./CommonSteps.module.scss";
import { useScrollAndFocusField } from "~/hooks";
import { usePassportCheck } from "../../../../shared/passport-check/usePassportCheck";
import { PassportCheckComponent } from "../../../../shared/passport-check";
import { FieldOffer } from "../../../../shared/field-offer";
import { useAnalyticsContext } from "~/services/analytics";
import { composeValidators, required } from "~/utils";
import { ERROR_FIELD_ } from "~/configs/consts";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 МБ
const ALLOWED_PASSPORT_TYPES = ['.jpg', '.jpeg', '.png', '.pdf', 'image/jpeg', 'image/png', 'application/pdf'];
const ALLOWED_AGREEMENT_TYPES = ['.doc', '.docx', '.odt', '.pdf', 'application/pdf'];
const ALLOWED_PRICES_TARIFFS_TYPES = ['.doc', '.docx', '.odt', '.xls', '.xlsx', '.csv', '.pdf',];

interface Props {
  isLoadingMount: boolean;
  isLoadingLogo: boolean;
  isLoadingPassport: boolean;
  logoFiles: File[];
  setLogoFileId: Dispatch<SetStateAction<number[]>>;
  setLogoFiles: Dispatch<File[]>;
  passportFiles: File[];
  setPassportFileId: Dispatch<SetStateAction<number[]>>;
  setPassportFiles: Dispatch<File[]>;
  logoError: AttachmentsDetailsError;
  passportError: AttachmentsDetailsError;
  setLogoError: Dispatch<AttachmentsDetailsError>;
  setPassportError: Dispatch<AttachmentsDetailsError>;
  COUNT_UPLOAD_LOGO_FILE?: number;
  COUNT_UPLOAD_PASSPORT_FILE?: number;
  setIsFirstLoadingFile: Dispatch<boolean>;
  orgType?: OrgTypeEnum;
  COUNT_DOCUMENT_AGREEMENT1_FILE?: number;
  COUNT_DOCUMENT_AGREEMENT2_FILE?: number;
  isLoadingDocumentAgreement1File: boolean;
  documentAgreement1Files: File[];
  setDocumentAgreement1FileId: Dispatch<SetStateAction<number[]>>;
  setDocumentAgreement1Files: Dispatch<File[]>;
  documentAgreement1FileIdRequiredError: AttachmentsDetailsError;
  documentAgreement1Error: AttachmentsDetailsError;
  setDocumentAgreement1Error: Dispatch<AttachmentsDetailsError>;
  isLoadingDocumentAgreement2File: boolean;
  documentAgreement2Files: File[];
  setDocumentAgreement2FileId: Dispatch<SetStateAction<number[]>>;
  setDocumentAgreement2Files: Dispatch<File[]>;
  documentAgreement2Error: AttachmentsDetailsError;
  setDocumentAgreement2Error: Dispatch<AttachmentsDetailsError>;
  textLinkAttachment: string;
  setTextLinkAttachment: Dispatch<SetStateAction<string>>;
  isLoadingDocumentPricesTariffsFile: boolean;
  documentPricesTariffsFiles: File[];
  setDocumentPricesTariffsFileId: Dispatch<SetStateAction<number[]>>;
  setDocumentPricesTariffsFiles: Dispatch<File[]>;
  documentPricesTariffsError: AttachmentsDetailsError;
  setDocumentPricesTariffsError: Dispatch<AttachmentsDetailsError>;
  pricesTariffsLinkError: AttachmentsDetailsError;
  setPricesTariffsLinkError: Dispatch<AttachmentsDetailsError>;
  passportMainFiles: File[];
  setPassportMainFiles: Dispatch<File[]>;
  passportRegistrationFiles: File[];
  setPassportRegistrationFiles: Dispatch<File[]>;
  passportMainFilesError: AttachmentsDetailsError;
  setPassportMainFilesError: Dispatch<AttachmentsDetailsError>;
  passportRegistrationFilesError: AttachmentsDetailsError;
  setPassportRegistrationFilesError: Dispatch<AttachmentsDetailsError>;
  setPassportMainFileId: Dispatch<SetStateAction<number[]>>;
  isLoadingPassportMainFileId: boolean;
  isLoadingPassportRegistrationFile: boolean;
  setPassportRegistrationFileId: Dispatch<SetStateAction<number[]>>;
  clientId: ClientId;
  passportMainFileId: any;
  passportRegistrationFileId: any;
  isUploadOffer: boolean;
  setIsUploadOffer: Dispatch<SetStateAction<boolean>>;
  errorUploadOffer: AttachmentsDetailsError;
  setErrorUploadOffer: Dispatch<AttachmentsDetailsError>;
  offer?: Offer;
  textLinkOffer: string;
  setTextLinkOffer: Dispatch<SetStateAction<string>>;
  isGeneratedOffer: boolean;
  setIsGeneratedOffer: Dispatch<SetStateAction<boolean>>;
  workType: "personal" | "joint";
  setWorkType: Dispatch<SetStateAction<"personal" | "joint">>;
  isWorkWithThirdParties: boolean;
}

export const AttachmentsDetails = ({
  isLoadingMount,
  setIsFirstLoadingFile,
  orgType,
  isLoadingDocumentAgreement1File,
  documentAgreement1Files,
  setDocumentAgreement1FileId,
  setDocumentAgreement1Files,
  documentAgreement1Error,
  documentAgreement1FileIdRequiredError,
  setDocumentAgreement1Error,
  isLoadingDocumentPricesTariffsFile,
  documentPricesTariffsFiles,
  setDocumentPricesTariffsFileId,
  setDocumentPricesTariffsFiles,
  documentPricesTariffsError,
  setDocumentPricesTariffsError,
  textLinkAttachment,
  setTextLinkAttachment,
  pricesTariffsLinkError,
  setPricesTariffsLinkError,
  passportMainFiles,
  setPassportMainFiles,
  passportRegistrationFiles,
  setPassportRegistrationFiles,
  passportMainFilesError,
  setPassportMainFilesError,
  passportRegistrationFilesError,
  setPassportRegistrationFilesError,
  setPassportMainFileId,
  isLoadingPassportMainFileId,
  isLoadingPassportRegistrationFile,
  setPassportRegistrationFileId,
  clientId,
  passportMainFileId,
  passportRegistrationFileId,
  isUploadOffer,
  setIsUploadOffer,
  errorUploadOffer,
  setErrorUploadOffer,
  offer,
  textLinkOffer,
  setTextLinkOffer,
  isGeneratedOffer,
  setIsGeneratedOffer,
  workType,
  setWorkType,
  isWorkWithThirdParties
}: Props) => {
  useScrollAndFocusField(NamesOfFieldEnum.PassportFileId);

  const analytics = useAnalyticsContext();
  analytics?.trackStep('anketa-customer-company-init_step6');

  useEffect(() => {
    if (analytics?.trackStepWebvisor && ((passportMainFiles && passportMainFiles.length > 0) || (passportRegistrationFiles && passportRegistrationFiles.length > 0))) {
      analytics?.trackStepWebvisor('webvisor_anketa_customer_step5');
    }
  }, [passportMainFiles, passportRegistrationFiles, analytics]);

  const passportMainFileIdSelected = passportMainFileId && passportMainFileId?.[0];
  const passportRegistrationFileIdSelected = passportRegistrationFileId && passportRegistrationFileId?.[0];

  const { status: statusMain, setStatus: setStatusMain, messages: messagesMain } = usePassportCheck({
    fileId: passportMainFileIdSelected,
    pageType: 'main',
    questionnaireUid: clientId,
    questionnaireType: 'ru_customer',
    shouldFetch: Boolean(passportMainFileIdSelected),
  });

  const { status: statusRegistration, setStatus: setStatusRegistration, messages: messagesRegistration } = usePassportCheck({
    fileId: passportRegistrationFileIdSelected,
    pageType: 'registration',
    questionnaireUid: clientId,
    questionnaireType: 'ru_customer',
    shouldFetch: Boolean(passportRegistrationFileIdSelected),
  });

  return (
    <Wizard.Page>
      <TitlePage name="Фуух. Осталось совсем чуть-чуть" number="6" isRenderIconHappy />
      <Divider style={{ margin: 0 }} />
      <div className={cn(styles.container, styles.containerUpload)}>
        {isLoadingMount ? (
          <div className={styles.containerLoader}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 24, color: "#3d935e" }} spin />} />
          </div>
        ) : (
          <>
            <div className={stylesStep.containerItem}>
              <TitleField
                name={configTitleFieldsOfCompany[NamesOfFieldEnum.PassportFileId]}
                required
              />
              <div className={stylesStep.itemHead}>
                <p className={styles.textImportant}>ВАЖНО:</p>
                <ul className={stylesStep.listRequired}>
                  <li>Необходимо предоставить скан/фото 2-3 страницы паспорта и страницу с действующей пропиской.</li>
                  <li>Данные должны быть четко видны (без засветов, закрытых элементов), особенно серия и номер паспорта.</li>
                </ul>
              </div>
              <div className={stylesStep.itemContent} style={{ display: 'flex', gap: '20px' }}>
                <div style={{ flex: '1 1 50%' }}>
                  <FormField
                    titleField={configTitleFieldsOfCompany[NamesOfFieldEnum.PassportFileId]}
                    name={NamesOfFieldEnum.PassportMainFileId}
                    required
                    renderError={passportMainFilesError}
                  >
                    <div>
                      <UploadFile
                        id={NamesOfFieldEnum.PassportMainFileId}
                        uploadedFiles={passportMainFiles}
                        setUploadedFiles={setPassportMainFiles}
                        isLoadingProps={isLoadingPassportMainFileId}
                        setFileid={setPassportMainFileId}
                        count={1}
                        error={passportMainFilesError}
                        setError={setPassportMainFilesError}
                        setIsFirstLoadingFile={setIsFirstLoadingFile}
                        contentText="Главный разворот"
                        isSecondaryLoading={isLoadingPassportRegistrationFile && !passportMainFiles?.length}
                        allowedFileTypes={ALLOWED_PASSPORT_TYPES}
                        maxFileSize={MAX_FILE_SIZE}
                        required
                      />
                    </div>
                    {/* <PassportCheckComponent status={statusMain} messages={messagesMain} /> */}
                  </FormField>
                </div>
                <div style={{ flex: '1 1 50%' }}>
                  <FormField
                    titleField={""}
                    name={NamesOfFieldEnum.PassportRegistrationFileId}
                    required
                    renderError={passportRegistrationFilesError}
                  >
                    <div>
                      <UploadFile
                        id={NamesOfFieldEnum.PassportRegistrationFileId}
                        uploadedFiles={passportRegistrationFiles}
                        setUploadedFiles={setPassportRegistrationFiles}
                        isLoadingProps={isLoadingPassportRegistrationFile}
                        setFileid={setPassportRegistrationFileId}
                        count={1}
                        error={passportRegistrationFilesError}
                        setError={setPassportRegistrationFilesError}
                        setIsFirstLoadingFile={setIsFirstLoadingFile}
                        contentText="Страница прописки"
                        isSecondaryLoading={isLoadingPassportMainFileId && !passportRegistrationFiles?.length}
                        allowedFileTypes={ALLOWED_PASSPORT_TYPES}
                        maxFileSize={MAX_FILE_SIZE}
                        required
                      />
                    </div>
                    {/* <PassportCheckComponent status={statusRegistration} messages={messagesRegistration} /> */}
                  </FormField>
                </div>
              </div>
            </div>

            <FeatureToggle
              flag="is_enabled_offer"
              NewComponent={
                <>
                  <FieldOffer
                    clientId={clientId}
                    isUploadOffer={isUploadOffer}
                    setIsUploadOffer={setIsUploadOffer}
                    errorUploadOffer={errorUploadOffer}
                    setErrorUploadOffer={setErrorUploadOffer}
                    typeOffer="default"
                    offer={offer}
                    textLinkOffer={textLinkOffer}
                    setTextLinkOffer={setTextLinkOffer}
                    setIsGeneratedOffer={setIsGeneratedOffer}
                    titleField={configTitleFieldsOfCompany[NamesOfFieldEnum.Offer]}
                    isGeneratedOffer={isGeneratedOffer}
                    shouldRenderTitleAboveField
                    required
                  />
                  <FormField
                    titleField={configTitleFieldsOfCompany[NamesOfFieldEnum.DocumentAgreement1NewFileId]}
                    name={NamesOfFieldEnum.DocumentAgreement1FileId}
                    styleContainer={{ minHeight: 190 }}
                    renderError={documentAgreement1Error?.has ? documentAgreement1Error : documentAgreement1FileIdRequiredError}
                    shouldRenderTitleAboveField
                    required={isWorkWithThirdParties}
                  >
                    <div
                      id={documentAgreement1Error?.has || documentAgreement1FileIdRequiredError?.has ? `${ERROR_FIELD_}DocumentAgreement1File` : undefined}
                    >
                      <p className={styles.textImportant}>ВАЖНО:</p>
                      <ul className={stylesStep.listRequired}>
                        <li>Если вы оказываете услуги совместно с третьим лицом, то необходимо предоставить договор услуг.</li>
                        <li>Чужие услуги и товары к реализации запрещены, но возможно привлечение третьих лиц через договор.</li>
                      </ul>

                      <div className={styles.containerRadioWorkTypeDocumentAgreement1File}>
                        <RadioField
                          classNameWrapper={styles.radioWorkTypeDocumentAgreement1File}
                          input={{
                            name: "workTypeDocumentAgreement1File",
                            value: "personal",
                            checked: workType === "personal",
                            onChange: () => setWorkType("personal"),
                          }}
                        >
                          Работаю лично
                        </RadioField>

                        <RadioField
                          classNameWrapper={styles.radioWorkTypeDocumentAgreement1File}
                          input={{
                            name: "workTypeDocumentAgreement1File",
                            value: "joint",
                            checked: workType === "joint",
                            onChange: () => setWorkType("joint"),
                          }}
                        >
                          Работаю совместно с 3-ми лицами
                        </RadioField>
                      </div>

                      <UploadFile
                        id={NamesOfFieldEnum.DocumentAgreement1FileId}
                        uploadedFiles={documentAgreement1Files}
                        setUploadedFiles={setDocumentAgreement1Files}
                        isLoadingProps={isLoadingDocumentAgreement1File}
                        setFileid={setDocumentAgreement1FileId}
                        count={1}
                        error={documentAgreement1Error}
                        setError={setDocumentAgreement1Error}
                        setIsFirstLoadingFile={setIsFirstLoadingFile}
                        allowedFileTypes={ALLOWED_AGREEMENT_TYPES}
                        maxFileSize={MAX_FILE_SIZE}
                        required={isWorkWithThirdParties}
                        buttonLayout="row"
                      />
                    </div>
                  </FormField>
                </>
              }
              OldComponent={
                <FormField
                  titleField={configTitleFieldsOfCompany[NamesOfFieldEnum.DocumentAgreement1FileId]}
                  name={NamesOfFieldEnum.DocumentAgreement1FileId}
                  styleContainer={{ minHeight: 190 }}
                  renderError={documentAgreement1Error}
                  shouldRenderTitleAboveField
                >
                  <div>
                    <p className={styles.textImportant}>ВАЖНО:</p>
                    <ul className={stylesStep.listRequired}>
                      <li>Если вы оказываете услуги совместно с третьим лицом, то необходимо предоставить договор услуг.</li>
                      <li>Чужие услуги и товары к реализации запрещены, но возможно привлечение третьих лиц через договор.</li>
                    </ul>
                    <UploadFile
                      id={NamesOfFieldEnum.DocumentAgreement1FileId}
                      uploadedFiles={documentAgreement1Files}
                      setUploadedFiles={setDocumentAgreement1Files}
                      isLoadingProps={isLoadingDocumentAgreement1File}
                      setFileid={setDocumentAgreement1FileId}
                      count={1}
                      error={documentAgreement1Error}
                      setError={setDocumentAgreement1Error}
                      setIsFirstLoadingFile={setIsFirstLoadingFile}
                      allowedFileTypes={ALLOWED_AGREEMENT_TYPES}
                      maxFileSize={MAX_FILE_SIZE}
                      required={isWorkWithThirdParties}
                      buttonLayout="row"
                    />
                  </div>
                </FormField>
              }
            />

            <FormField
              titleField={configTitleFieldsOfCompany[NamesOfFieldEnum.DocumentPricesTariffsFileId]}
              name={NamesOfFieldEnum.DocumentPricesTariffsFileId}
              styleContainer={{ minHeight: 140 }}
              renderError={documentPricesTariffsError}
              shouldRenderTitleAboveField
            >
              <div>
                <p className={styles.textImportant}>ВАЖНО:</p>
                <ul className={stylesStep.listRequired}>
                  <li>Загрузите ваши прайсы или приложите ссылку с актуальными тарифами на ваши услуги.</li>
                </ul>

                <UploadFile
                  id={NamesOfFieldEnum.DocumentPricesTariffsFileId}
                  uploadedFiles={documentPricesTariffsFiles}
                  setUploadedFiles={setDocumentPricesTariffsFiles}
                  isLoadingProps={isLoadingDocumentPricesTariffsFile}
                  setFileid={setDocumentPricesTariffsFileId}
                  count={1}
                  uploadButtonWidth={140}
                  error={documentPricesTariffsError}
                  setError={setDocumentPricesTariffsError}
                  setIsFirstLoadingFile={setIsFirstLoadingFile}
                  prefixComponent={
                    <>
                      <div className={styles.containerinputWithFileAttachment}>
                        <input
                          className={styles.inputWithFileAttachment}
                          value={textLinkAttachment}
                          id={pricesTariffsLinkError?.has ? `${ERROR_FIELD_}DocumentPricesTariffsFile` : undefined}
                          onChange={
                            (e) => {
                              setTextLinkAttachment(e.target.value)
                              setPricesTariffsLinkError(null)
                            }
                          }
                          placeholder="Введите ссылку"
                        />
                        {pricesTariffsLinkError && <ErrorField name="prices_tariffs_link" renderError={pricesTariffsLinkError} />}
                      </div>
                      <span className={styles.orText}>или</span>
                    </>
                  }
                  allowedFileTypes={ALLOWED_PRICES_TARIFFS_TYPES}
                  maxFileSize={MAX_FILE_SIZE}
                  buttonLayout="row"
                />
              </div>
            </FormField>
          </>
        )}
      </div>
      <RulesFiles />
      <Divider style={{ margin: 0 }} />
      <AcceptPolitics />
    </Wizard.Page>
  );
};
